<template>
  <div class="account-add-dialog">
    <el-dialog custom-class="with-title" :title="title" :visible.sync="visible">
      <div class="dialog-content">
        <div v-if="!isAuth">
          <div class="message message-info mb-x3">
            キャンペーンの作成には、アカウント認証が必要です。<br />応募の取得や当選者連絡などに認証情報を利用します。
          </div>
          <div class="sns-select" layout>
            <div class="card mr-x1" flex="1" layout="row" layout-align="center" @click="startTwitterAuth">
              <img class="mr-x2" src="@/client/assets/images/x-with-circle.svg" />
              <label>X</label>
            </div>
            <div class="card ml-x1" flex="1" layout="row" layout-align="center" @click="startInstagramAuth">
              <img class="mr-x2" src="@/client/assets/images/instagram-with-circle.svg" />
              <label>Instagram</label>
            </div>
            <div class="card ml-x1" flex="1" layout="row" layout-align="center" @click="startTiktokAuth">
              <img class="mr-x2" src="@/client/assets/images/tiktok-with-circle.svg" />
              <label>TikTok</label>
            </div>
          </div>
        </div>
        <div v-else>
          <div v-if="authType === 'twitter'">
            <div class="mb-x2">以下のアカウントを登録してよろしいですか?</div>
            <icon
              class="mr-x2"
              :name="twitterAccount.name"
              :pictureUrl="twitterAccount.pictureUrl"
              iconType="twitter"
            ></icon
            >{{ twitterAccount.name }}
          </div>
          <div v-else-if="authType === 'tiktok'">
            <div class="mb-x2">以下のアカウントを登録してよろしいですか?</div>
            <icon
              class="mr-x2"
              :name="tiktokAccount.name"
              :pictureUrl="tiktokAccount.pictureUrl"
              iconType="tiktok"
            ></icon
            >{{ tiktokAccount.name }}
          </div>
        </div>
      </div>
      <div class="dialog-footer" slot="footer" v-if="isAuth">
        <el-button type="default" @click="close">キャンセル</el-button>
        <el-button type="primary" @click="addAccount" :disabled="!canAddAccount"
          ><i class="mdi mdi-plus"></i>登録</el-button
        >
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.sns-select {
  .card {
    text-align: center;
    cursor: pointer;

    &:hover {
      border-color: $primary-color;
    }

    img {
      width: 40px;
      height: 40px;
    }

    label {
      padding-top: 8px;
      font-size: 1.1em;
      font-weight: bold;
      cursor: pointer;
    }
  }
}
</style>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import Icon from '@/client/components/_atoms/basic_icon'
import api from '@/client/core/api'
import { TikTokAuthService } from '@/client/core/tiktok_auth'
import TwitterAuthService from '@/client/core/twitter_auth'
import { useSettingGroupStore } from '@/client/store/useSettingGroupStore'
import { useSystemStore } from '@/client/store/useSystemStore'

export default defineComponent({
  components: {
    Icon
  },
  data() {
    return {
      visible: false,
      title: 'キャンペーン実施アカウントの追加',
      authType: '',
      twitterAccount: null,
      tiktokAccount: null,
      isAuth: false,
      isSubmit: false
    }
  },
  computed: {
    ...mapState(useSystemStore, {
      currentGroupId: state => state.currentGroup.id,
      allowInstantwin: state => state.currentGroup.allowInstantwin
    }),
    canAddAccount() {
      if (this.isSubmit) {
        return false
      }

      if (this.authType === 'twitter' && this.twitterAccount) {
        return true
      }

      if (this.authType === 'tiktok' && this.tiktokAccount) {
        return true
      }

      return false
    }
  },
  methods: {
    ...mapActions(useSettingGroupStore, ['fetchAccounts']),
    /**
     * 認証を行う
     */
    async startTwitterAuth() {
      this.twitterAccount = null
      this.isAuth = false
      this.authType = 'twitter'
      const account = await TwitterAuthService.execAuth()

      if (!account) {
        this.$notify({
          title: '認証に失敗しました。',
          message: '恐れ入りますが、時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      const result = await api.get('/twitter_campaign_accounts/' + account.id + '/exists', {
        params: { groupId: this.currentGroupId }
      })

      if (result.data === true) {
        return this.$notify({
          title: `${account.name}はすでに登録済みです。`,
          customClass: 'danger',
          duration: 5000
        })
      }

      this.twitterAccount = account

      this.isAuth = true
    },
    /**
     * Tiktok認証をする
     */
    async startTiktokAuth() {
      this.twitterAccount = null
      this.isAuth = false
      this.authType = 'tiktok'
      const callback = await TikTokAuthService.openAuthWindow()

      if (callback.type !== 'SUCCESS') {
        this.$notify({
          title: '認証に失敗しました。',
          message: '恐れ入りますが、時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      const ttAccount = await TikTokAuthService.getAccountInfo(callback, this.currentGroupId)

      if (!ttAccount && !ttAccount.data) {
        return this.$notify({
          title: '認証に失敗しました。',
          message: '恐れ入りますが、時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
      }

      if (ttAccount.data.isExist) {
        return this.$notify({
          title: `${ttAccount.data.name}はすでに登録済みです。`,
          customClass: 'danger',
          duration: 5000
        })
      }

      this.tiktokAccount = ttAccount.data

      this.isAuth = true
    },

    /**
     * アカウントを追加
     */
    async addAccount() {
      if (this.authType === 'twitter') {
        await this.addTwitterAccount()
      }

      if (this.authType === 'tiktok') {
        await this.addTikTokAccount()
      }

      // アカウント一覧を取得
      const { shouldLeaveGroupSetting } = await this.fetchAccounts(this.currentGroupId)
      this.close()

      if (shouldLeaveGroupSetting) {
        this.$router.push('/campaigns')
      }
    },

    /**
     * キャンペーン実施TWアカウントを追加
     */
    async addTwitterAccount() {
      if (this.isSubmit) {
        return
      }

      const params = {
        ...this.twitterAccount,
        accountId: this.twitterAccount.id,
        groupId: this.currentGroupId
      }

      this.isSubmit = true

      const result = await api.post('/twitter_campaign_accounts', params)

      this.isSubmit = false

      if (result.error && result.error.type === 'DUPLICATE') {
        this.$notify({
          title: `${this.twitterAccount.name}はすでに登録済みです。`,
          customClass: 'danger',
          duration: 5000
        })
        return
      }
      if (result.error && result.error.type === 'MAX_ACCOUNT_OVER') {
        this.$notify({
          title: 'これ以上アカウントを追加できません。',
          message: 'グループの登録可能アカウント数を超えています。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      if (!result.data) {
        this.$notify({
          title: 'アカウント追加処理に失敗しました。',
          message: '恐れ入りますが、時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'アカウントを追加しました。',
        message: `${this.twitterAccount.name}を追加しました。`,
        customClass: 'success',
        duration: 5000
      })
    },

    /**
     * キャンペーン実施TikTokアカウントを追加
     */
    async addTikTokAccount() {
      if (this.isSubmit) {
        return
      }

      const body = {
        accessToken: this.tiktokAccount.accessToken,
        refreshToken: this.tiktokAccount.refreshToken,
        groupId: this.currentGroupId
      }

      this.isSubmit = true

      const result = await api.post('/tiktok_campaign_accounts', body)

      this.isSubmit = false

      if (result.error && result.error.type === 'DUPLICATE') {
        this.$notify({
          title: `${this.tiktokAccount.name}はすでに登録済みです。`,
          customClass: 'danger',
          duration: 5000
        })
        return
      }
      if (result.error && result.error.type === 'MAX_ACCOUNT_OVER') {
        this.$notify({
          title: 'これ以上アカウントを追加できません。',
          message: 'グループの登録可能アカウント数を超えています。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      if (!result.data) {
        this.$notify({
          title: 'アカウント追加処理に失敗しました。',
          message: '恐れ入りますが、時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'アカウントを追加しました。',
        message: `${this.tiktokAccount.name}を追加しました。`,
        customClass: 'success',
        duration: 5000
      })
    },

    /**
     * Instagram1回目の認証
     */
    async startInstagramAuth() {
      this.authType = 'instagram'
      this.$emit('next')
    },

    /**
     * ダイアログをあく
     */
    open() {
      this.visible = true
      this.twitterAccount = null
      this.isAuth = false
    },
    /**
     * ダイアログを閉じる
     */
    close() {
      this.visible = false
    }
  }
})
</script>
